import { FC, ReactElement, useEffect } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import type { TUserRole } from 'interfaces/Role.interface';
import useNotification from 'hooks/useNotification.hook';
import { useCheckAuth } from 'hooks/auth/useCheckAuth.hook';

export interface IRequireAuthProps {
  children: ReactElement;
  requiredRoles?: TUserRole[];
  redirectPath?: string;
  silentCheck?: boolean;
}

const RequireAuth: FC<IRequireAuthProps> = ({
  children,
  requiredRoles = [],
  redirectPath = '/login',
  silentCheck = false,
}) => {
  const { t } = useTranslation();
  const { check, checkPasswordChangeRequired } = useCheckAuth();
  const { set } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (checkPasswordChangeRequired()) {
      navigate('/password-update', { state: { from: location } });
    }
    if (!check(requiredRoles) && !silentCheck) {
      set({
        type: 'error',
        message: t('errors:error.notAuthorized'),
      });
    }
  }, []);

  if (!check(requiredRoles)) {
    return (
      <Navigate
        to={{
          pathname: redirectPath,
          search: `?redirect=${location.pathname}${location.search}`,
        }}
        state={{ from: location }}
      />
    );
  }
  return children;
};

export default RequireAuth;
